import { SkillMetadata } from "@models";
import {
  Add,
  AutoAwesome,
  ExtensionOutlined,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Link,
  Settings,
  UploadFile,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import type {
  ChangeEvent,
  CSSProperties,
  FocusEvent,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "src/config";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { useCreateSkillMutation, useGetSkills, useGetUser } from "src/hooks";
import { fillRouteWithSlugs } from "src/utils";
import {
  canCreateInternalSkill,
  handleSkillMarkdownUpload,
  isAdminUser,
} from "../skill-utils";
import {
  SkillFormModal,
  SkillFormMode,
  SkillFormValues,
  toSkillWritePayload,
} from "../SkillFormModal";
import { useOpenSkillDraftAgentPanel } from "../useOpenSkillDraftAgentPanel";
import { MenuSkillsContent } from "./MenuSkills";

type MenuSize = {
  width: number;
  height: number;
};

const MENU_UNMOUNT_DELAY_MS = import.meta.env.MODE === "test" ? 0 : 100;
const ROOT_MENU_LIST_SX = {
  p: 0,
  overflow: "hidden",
};
const MATCHED_ROOT_MENU_LIST_SX = {
  boxSizing: "border-box",
  height: "100%",
  minHeight: 0,
};
const TRIGGER_ICON_SX = {
  alignItems: "center",
  color: "inherit",
  display: "inline-flex",
  justifyContent: "center",
  lineHeight: 0,
  "& .MuiSvgIcon-root, & svg": {
    color: "inherit",
  },
};
const MENU_CONTAINER_STYLE: CSSProperties = {
  paddingTop: 0,
  paddingBottom: "4px",
};
const MENU_VIEW_STYLE: CSSProperties = {
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  overflow: "hidden",
  paddingTop: 5,
  paddingBottom: 5,
};
const BACK_BUTTON_STYLE: CSSProperties = {
  alignSelf: "flex-start",
  color: "rgba(0, 0, 0, 0.87)",
  fontSize: "0.8125rem",
  fontWeight: 400,
  justifyContent: "flex-start",
  lineHeight: 1.2,
  margin: "4px 6px",
  minWidth: 0,
  padding: 0,
  textTransform: "none",
};
const MENU_ITEM_STYLE: CSSProperties = {
  padding: "6px 10px",
  margin: "4px 6px",
};

// react-doctor-disable-next-line react-doctor/prefer-useReducer -- The menu keeps independent view flags and async status; a reducer would add indirection without reducing branching.
export function ChatSkillsMenu({
  orgId,
  attachedSkills,
  onAttachedSkillsChange,
  onCreateSkillFromChat,
  isCreateSkillFromChatDisabled = false,
}: {
  orgId: string;
  attachedSkills: SkillMetadata[];
  onAttachedSkillsChange: (skills: SkillMetadata[]) => void;
  onCreateSkillFromChat: () => void;
  isCreateSkillFromChatDisabled?: boolean;
}) {
  const navigate = useNavigate();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);
  const unmountTimerRef = useRef<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isMenuMounted, setIsMenuMounted] = useState(false);
  const [isAttachViewOpen, setIsAttachViewOpen] = useState(false);
  const [attachSearch, setAttachSearch] = useState("");
  const [formMode, setFormMode] = useState<SkillFormMode | null>(null);
  const [formInitialValues, setFormInitialValues] =
    useState<Partial<SkillFormValues>>();
  const [rootMenuSize, setRootMenuSize] = useState<MenuSize | null>(null);
  const [isTriggerTooltipOpen, setIsTriggerTooltipOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMenuOpen = Boolean(anchorEl);

  const { data: user } = useGetUser();
  const { data: skills = [] } = useGetSkills({
    orgId,
    search: attachSearch,
    enabled: isMenuOpen && isAttachViewOpen,
  });
  const createSkill = useCreateSkillMutation();
  const openSkillDraftAgentPanel = useOpenSkillDraftAgentPanel();

  const resetAttachView = () => {
    setIsAttachViewOpen(false);
    setAttachSearch("");
  };

  const clearUnmountTimer = () => {
    if (unmountTimerRef.current === null) {
      return;
    }

    window.clearTimeout(unmountTimerRef.current);
    unmountTimerRef.current = null;
  };

  const scheduleMenuUnmount = () => {
    clearUnmountTimer();

    unmountTimerRef.current = window.setTimeout(() => {
      unmountTimerRef.current = null;
      setIsMenuMounted(false);
    }, MENU_UNMOUNT_DELAY_MS);
  };

  const closeTriggerTooltip = () => {
    setIsTriggerTooltipOpen(false);
  };

  const openTriggerTooltip = () => {
    if (!isMenuOpen) {
      setIsTriggerTooltipOpen(true);
    }
  };

  const closeMenu = () => {
    closeTriggerTooltip();
    setAnchorEl(null);
    setRootMenuSize(null);
    resetAttachView();
    scheduleMenuUnmount();
  };

  const openMenu = (event: ReactMouseEvent<HTMLButtonElement>) => {
    closeTriggerTooltip();
    triggerRef.current = event.currentTarget;
    clearUnmountTimer();
    setIsMenuMounted(true);
    setRootMenuSize(null);
    resetAttachView();
    setAnchorEl(event.currentTarget);
  };

  const closeForm = () => {
    setFormMode(null);
    setFormInitialValues(undefined);
  };

  const closeErrorDialog = () => {
    setError(null);
  };

  const focusMenuInput = (event: FocusEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) {
      event.currentTarget.querySelector("input")?.focus();
    }
  };

  const openCreate = () => {
    setError(null);
    setFormMode("create");
  };

  const openCreateFromChat = () => {
    if (isCreateSkillFromChatDisabled) {
      return;
    }

    setError(null);
    onCreateSkillFromChat();
  };

  const openUpload = () => {
    uploadInputRef.current?.click();
  };

  const openAttach = () => {
    setAttachSearch("");
  };

  const openManage = () => {
    navigate(
      fillRouteWithSlugs(ROUTES.SKILLS, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
      })
    );
  };

  const measureRootMenuSize = () => {
    const container = menuContainerRef.current;

    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const width = Math.ceil(
      rect.width || container.offsetWidth || container.scrollWidth
    );
    const height = Math.ceil(
      rect.height || container.offsetHeight || container.scrollHeight
    );

    if (width <= 0 || height <= 0) {
      return;
    }

    setRootMenuSize((currentSize) => {
      if (currentSize?.width === width && currentSize.height === height) {
        return currentSize;
      }

      return { width, height };
    });
  };

  const openAttachView = () => {
    openAttach();
    measureRootMenuSize();
    setIsAttachViewOpen(true);
  };

  const goBackToRootView = () => {
    resetAttachView();
  };

  const runMenuAction = (action: () => void) => {
    action();
    closeMenu();
  };

  const handleAttachItemKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!["ArrowRight", "Enter", " "].includes(event.key)) {
      return;
    }

    event.preventDefault();
    openAttachView();
  };

  const attachSkill = (skill: SkillMetadata, closeMenu: () => void) => {
    if (attachedSkills.some((attached) => attached.id === skill.id)) {
      return;
    }

    onAttachedSkillsChange([...attachedSkills, skill]);
    closeMenu();
  };

  const renderAttachSkillsContent = ({
    closeMenu,
  }: {
    closeMenu: () => void;
  }) => (
    <MenuSkillsContent
      isMenuOpen={isMenuOpen && isAttachViewOpen}
      searchQuery={attachSearch}
      setSearchQuery={setAttachSearch}
      filteredSkills={skills}
      attachedSkills={attachedSkills}
      handleSkillSelect={(skill) => attachSkill(skill, closeMenu)}
    />
  );

  const draftSkillWithAgent = () => {
    closeForm();
    openSkillDraftAgentPanel({ operation: "create" });
  };

  const saveSkill = async (values: SkillFormValues) => {
    try {
      await createSkill.mutateAsync({
        orgId,
        data: toSkillWritePayload(values),
      });
      closeForm();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not save the skill."
      );
    }
  };

  const handleUpload = (event: ChangeEvent<HTMLInputElement>) =>
    handleSkillMarkdownUpload({
      file: event.target.files?.[0],
      input: uploadInputRef.current,
      onDraft: (draft) => {
        setFormMode("upload");
        setFormInitialValues(draft);
      },
      onError: setError,
    });

  useEffect(() => {
    return () => {
      clearUnmountTimer();
    };
  }, []);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      closeMenu();
      triggerRef.current?.focus();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  useLayoutEffect(() => {
    if (!isMenuOpen || isAttachViewOpen) {
      return;
    }

    measureRootMenuSize();
  });

  const shouldApplyRootSize = isAttachViewOpen && rootMenuSize !== null;
  const matchedRootSizeSx = shouldApplyRootSize
    ? {
        boxSizing: "border-box",
        width: `${rootMenuSize.width}px`,
        height: `${rootMenuSize.height}px`,
      }
    : undefined;

  return (
    <>
      <Tooltip
        title="Skills"
        open={isTriggerTooltipOpen && !isMenuOpen}
        onClose={closeTriggerTooltip}
        onOpen={openTriggerTooltip}
        TransitionProps={{ timeout: 0 }}
      >
        <IconButton
          id="skills-button"
          ref={triggerRef}
          size="small"
          aria-controls={isMenuOpen ? "chat-skills-menu" : undefined}
          aria-expanded={isMenuOpen ? "true" : undefined}
          aria-haspopup="menu"
          aria-label="Skills"
          className="size-6 rounded-md border border-solid border-zinc-300 p-1"
          onClick={openMenu}
          onMouseDown={closeTriggerTooltip}
          sx={{
            color: "#6b7280",
          }}
        >
          <Box component="span" sx={TRIGGER_ICON_SX}>
            <ExtensionOutlined className="size-[16px] text-zinc-500" />
          </Box>
        </IconButton>
      </Tooltip>

      {isMenuMounted ? (
        <Menu
          id="chat-skills-menu"
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={closeMenu}
          autoFocus={false}
          transitionDuration={MENU_UNMOUNT_DELAY_MS}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          MenuListProps={{
            "aria-labelledby": "skills-button",
            sx: [
              ROOT_MENU_LIST_SX,
              ...(shouldApplyRootSize ? [MATCHED_ROOT_MENU_LIST_SX] : []),
            ],
          }}
          slotProps={{
            root: {
              disableAutoFocus: true,
            },
            paper: {
              onFocus: focusMenuInput,
              sx: [
                {
                  overflow: "hidden",
                },
                ...(matchedRootSizeSx ? [matchedRootSizeSx] : []),
              ],
            },
          }}
        >
          <Box
            ref={menuContainerRef}
            className="chat-skills-menu-container overflow-hidden"
            style={{
              ...MENU_CONTAINER_STYLE,
              ...(shouldApplyRootSize
                ? {
                    boxSizing: "border-box",
                    width: rootMenuSize.width,
                    height: "100%",
                    minHeight: 0,
                  }
                : {}),
            }}
          >
            <Box className="flex flex-col py-2" style={MENU_VIEW_STYLE}>
              {isAttachViewOpen ? (
                <>
                  <Button
                    onClick={goBackToRootView}
                    startIcon={
                      <KeyboardArrowLeft sx={{ fontSize: "0.875rem" }} />
                    }
                    style={BACK_BUTTON_STYLE}
                    variant="text"
                  >
                    Go back
                  </Button>
                  <Divider style={{ marginBottom: 8 }} />
                  {renderAttachSkillsContent({ closeMenu })}
                </>
              ) : (
                <>
                  <MenuItem
                    onClick={() => runMenuAction(openCreate)}
                    style={MENU_ITEM_STYLE}
                  >
                    <Add fontSize="small" sx={{ mr: 1 }} />
                    Create skill
                  </MenuItem>
                  <MenuItem
                    onClick={() => runMenuAction(openCreateFromChat)}
                    disabled={isCreateSkillFromChatDisabled}
                    style={MENU_ITEM_STYLE}
                  >
                    <AutoAwesome fontSize="small" sx={{ mr: 1 }} />
                    Create skill from chat
                  </MenuItem>
                  <MenuItem
                    onClick={() => runMenuAction(openUpload)}
                    style={MENU_ITEM_STYLE}
                  >
                    <UploadFile fontSize="small" sx={{ mr: 1 }} />
                    Upload skill (.md)
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    id="attach-skill-menu-item"
                    onClick={openAttachView}
                    onKeyDown={handleAttachItemKeyDown}
                    className="gap-3"
                    style={MENU_ITEM_STYLE}
                  >
                    <Link fontSize="small" sx={{ mr: 1 }} />
                    <span className="flex-1">Attach skill to run</span>
                    <KeyboardArrowRight
                      data-testid="attach-skill-caret"
                      fontSize="small"
                      sx={{ color: "rgba(0, 0, 0, 0.54)" }}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => runMenuAction(openManage)}
                    style={MENU_ITEM_STYLE}
                  >
                    <Settings fontSize="small" sx={{ mr: 1 }} />
                    Manage skills
                  </MenuItem>
                </>
              )}
            </Box>
          </Box>
        </Menu>
      ) : null}

      {error ? (
        <Dialog
          open={!!error}
          onClose={closeErrorDialog}
          fullWidth
          maxWidth="xs"
        >
          <DialogContent>
            <Alert severity="error" onClose={closeErrorDialog}>
              {error}
            </Alert>
          </DialogContent>
        </Dialog>
      ) : null}

      <SkillFormModal
        state={{
          isOpen: !!formMode,
          isLoading: createSkill.isPending,
          draftLoading: false,
        }}
        mode={formMode ?? "create"}
        initialValues={formInitialValues}
        permissions={{
          canCreateGlobal: isAdminUser(user),
          canCreateFormlyInternal: canCreateInternalSkill(user),
        }}
        onCancel={closeForm}
        onDraftWithAgent={
          formMode === "create" ? draftSkillWithAgent : undefined
        }
        onSave={saveSkill}
      />

      <input
        ref={uploadInputRef}
        type="file"
        accept=".md,text/markdown,text/plain"
        hidden
        onChange={handleUpload}
      />
    </>
  );
}
